import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { fadeInUpVariants, easings } from '../../hooks/useScrollAnimation';
import { FiGithub, FiExternalLink } from 'react-icons/fi';
import { FaGooglePlay } from 'react-icons/fa';
import { ImageWithLoader } from '../shared';
import ProjectDetail from './ProjectDetail';

const ProjectsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.section} ${({ theme }) => theme.spacing.xl};
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxxl} ${({ theme }) => theme.spacing.lg};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxxl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing.xxl};
  }
`;

const Kicker = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 2px;
  text-transform: uppercase;
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.display};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.1;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ProjectsGrid = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.xxl};
  }
`;

const ProjectCard = styled(motion.div)`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: ${({ theme }) => theme.spacing.xxxl};
  align-items: center;
  opacity: 0;
  padding: ${({ theme }) => theme.spacing.xl};
  border: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  transition:
    border-color 0.3s ease,
    background-color 0.3s ease,
    transform 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  /* Make sure internal elements don't block clicks */
  cursor: pointer;

  &:nth-child(even) {
    grid-template-columns: 1fr 1.4fr;
  }

  &:nth-child(even) .project-content {
    order: -1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    padding: ${({ theme }) => theme.spacing.lg};

    &:nth-child(even) {
      grid-template-columns: 1fr;
    }

    &:nth-child(even) .project-content {
      order: 0;
    }
  }
`;

const ProjectImage = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 400px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 250px;
  }
`;

const ProjectContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const ProjectNumber = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 2px;
`;

const ProjectTitle = styled.h3`
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.display};
  line-height: 1.2;
`;

const ProjectDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  max-width: 600px;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const TechTag = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const ProjectLink = styled(motion.a)`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.5rem;
  transition: color 0.3s ease;
  position: relative;
  padding-bottom: 4px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: ${({ theme }) => theme.colors.textSecondary};
    transform: scaleX(0);
    transition:
      transform 0.3s ease,
      background 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover::after {
    transform: scaleX(1);
    background: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.25rem;
  }
`;

export interface Project {
  title: string;
  description: string;
  tech: string[];
  image?: string;
  images?: string[];
  github?: string;
  playStore?: string;
  live?: string;
}

const projectsData: Project[] = [
  {
    title: 'Middy',
    description:
      'A social selling mobile application that enables users to discover and purchase products through video feeds. Implemented custom optimized video scrolling, UI components, and animations.',
    tech: ['React Native', 'Redux', 'Node.js', 'MongoDB'],
    image: 'https://i.ibb.co/xYWkJbF/Products-Scrolled.png',
    images: [
      'https://i.ibb.co/xYWkJbF/Products-Scrolled.png',
      'https://i.ibb.co/5h2Tqxq/Signup-Login-24-Light.png',
      'https://i.ibb.co/Jm4xF2x/Follow.png',
      'https://i.ibb.co/XYYqPVR/Original.png',
      'https://i.ibb.co/mGkPrxH/Screenshot-2024-11-17-at-21-00-29.png',
    ],
  },
  {
    title: 'Vinkel',
    description:
      'Real-time delivery tracking app with live order updates, restaurant discovery, and secure payment integration. Built for high scalability and performance.',
    tech: ['React Native', 'GraphQL', 'Firebase', 'Stripe'],
    image: 'https://i.ibb.co/nn2Br86/1st-page.png',
    images: [
      'https://i.ibb.co/nn2Br86/1st-page.png',
      'https://i.ibb.co/jgHYKZ7/earning-page-monthly-subscription.png',
      'https://i.ibb.co/YdDKwY6/job-delivery-page.png',
      'https://i.ibb.co/Dg5pd3s/Pickup-screen.png',
      'https://i.ibb.co/wJg3Sff/New-job-request.png',
    ],
  },
  {
    title: 'Elfeedback',
    description:
      'An opinion-collecting app that allows users to share their experience and feedback. Optimized for speed and performance with real-time capabilities.',
    tech: ['React Native', 'Firebase', 'MobX', 'REST'],
    image: 'https://i.ibb.co/0yH2GKQ/Onboarding-step1.png',
    images: [
      'https://i.ibb.co/0yH2GKQ/Onboarding-step1.png',
      'https://i.ibb.co/xqs31sn/notification-on.png',
      'https://i.ibb.co/dJXcr8X/OTP-code.png',
      'https://i.ibb.co/D9ttPKb/Onboarding-step2.jpg',
    ],
  },
];

const Projects: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
  };

  const handleCloseDetail = () => {
    setSelectedProject(null);
  };

  return (
    <ProjectsSection id="projects">
      <SectionHeader>
        <Kicker>Selected Work</Kicker>
        <SectionTitle
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUpVariants}
        >
          Featured Projects
        </SectionTitle>
      </SectionHeader>

      <ProjectsGrid
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.15,
              ease: easings.easeOutQuint,
            },
          },
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {projectsData.map((project, index) => (
          <ProjectCard
            key={index}
            variants={fadeInUpVariants}
            onClick={() => handleProjectClick(project)}
            whileHover={{ scale: 1.01, y: -2 }}
            whileTap={{ scale: 0.99 }}
            style={{ backgroundColor: 'var(--color-surface)' }}
          >
            <ProjectImage className="project-image">
              {project.image && (
                <ImageWithLoader
                  src={project.image}
                  alt={project.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
            </ProjectImage>

            <ProjectContent className="project-content">
              <ProjectNumber>0{index + 1}</ProjectNumber>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectTech>
                {project.tech.map((tech, i) => (
                  <TechTag key={i}>{tech}</TechTag>
                ))}
              </ProjectTech>
              <ProjectLinks>
                <ProjectLink
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiGithub />
                </ProjectLink>
                <ProjectLink
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaGooglePlay />
                </ProjectLink>
                <ProjectLink
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiExternalLink />
                </ProjectLink>
              </ProjectLinks>
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>
      <ProjectDetail
        project={selectedProject}
        isOpen={selectedProject !== null}
        onClose={handleCloseDetail}
      />
    </ProjectsSection>
  );
};

export default Projects;
